import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Box,
    Divider,
    FormControl,
    InputLabel,
    ListSubheader,
    Select,
    SelectChangeEvent,
    Tabs,
    Tooltip,
    Typography,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';

import {
    FleetSafetyScores,
    FleetScoreDetails,
    FleetScoreShort,
    FleetTopBottomDrivers,
    OverviewData,
} from '../../../backendsdk';
import { TabPanel, a11yProps } from '../../../components/Tab';
import { TrackedMenuItem as MenuItem, TrackedTab as Tab } from '../../../components/TrackedComponents';
import useApi from '../../../hooks/api';
import useDrivers from '../../../hooks/drivers';
import useIsMobile from '../../../hooks/isMobile';
import useProfile from '../../../hooks/profile';
import { accidentsMetricsPageDefs, csaScoresPageDefs, safetyMetricsPageDefs } from '../../../utils/Pages';
import palette from '../../ColorPalette';
import { DEFAULT_TITLE } from '../../Layout';
import AccidentOverview from './AccidentOverview/AccidentOverview';
import CsaScoresComponent from './CsaScores/CsaScoresComponent';
import MetricsComponent from './SafetyWellness/MetricsComponent';

export const ANIMATION_LENGTH = 1000;

const TABS = ['safety', 'accidents', 'csa_scores'];
const PAGE_DEFS = [safetyMetricsPageDefs, accidentsMetricsPageDefs, csaScoresPageDefs];

export type QuarterYear = { quarter: number; year: number };

const getPreviousQuarters = (date: Dayjs, quartersCount?: number): QuarterYear[] => {
    const quarters: { quarter: number; year: number }[] = [];
    const currentQuarter = getQuarterAndYear(date);
    quarters.push(currentQuarter);

    let prevQuarter = { quarter: currentQuarter.quarter, year: currentQuarter.year };
    for (let i = 0; i < (quartersCount ?? 4); i++) {
        prevQuarter = {
            quarter: prevQuarter.quarter - 1 === 0 ? 4 : prevQuarter.quarter - 1,
            year: prevQuarter.quarter - 1 === 0 ? prevQuarter.year - 1 : prevQuarter.year,
        };
        quarters.push(prevQuarter);
    }

    return quarters;
};

const getQuarterAndYear = (date: Dayjs): QuarterYear => {
    const month = date.month(); // 0-11
    const year = date.year();
    const quarter = Math.floor(month / 3) + 1;
    return { quarter, year };
};

export const getQuarterTimestamps = (quarterYear: QuarterYear): [number, number] => {
    const { quarter, year } = quarterYear;
    if (quarter === 0 && year === 0) {
        const today = dayjs.utc().startOf('day');
        return [today.subtract(3, 'month').unix(), today.unix()];
    }
    const nextQuarter = quarter === 4 ? 1 : quarter + 1;
    const nextYear = quarter === 4 ? year + 1 : year;
    const firstMonthOfQuarter = (nextQuarter - 1) * 3 + 1;
    const startOfNextQuarter = dayjs.utc(`${nextYear}-${firstMonthOfQuarter}-01`);
    return [startOfNextQuarter.subtract(3, 'month').unix(), startOfNextQuarter.unix()];
};

const MetricsContent: React.FC = () => {
    const { t } = useTranslation();
    const { profile } = useProfile();
    const ALL_FLEET = t('content.metrics.all_fleet');
    const [selectedSubFleet, setSelectedSubFleet] = useState<string>(
        profile.sub_fleets.length > 0 ? profile.sub_fleets[0] : ALL_FLEET,
    );
    const match = useRouteMatch();
    const metricsTab = match.path.split('/')[2];
    const initialTab = metricsTab !== undefined && TABS.includes(metricsTab) ? TABS.indexOf(metricsTab) : 0;
    const [tab, setTab] = useState<number>(initialTab);
    const { api, agencyApi } = useApi();
    const [isMetricsLoading, setIsMetricsLoading] = useState<boolean>(true);
    const [isMetricsError, setIsMetricsError] = useState<boolean>(false);
    const [isOverviewLoading, setIsOverviewLoading] = useState<boolean>(true);
    const [isOverviewError, setIsOverviewError] = useState<boolean>(false);
    const [fleetScoreDetails, setFleetScoreDetails] = useState<Record<string, FleetScoreDetails>>({});
    const [fleetScoreTrend, setFleetScoreTrend] = useState<FleetScoreShort[]>([]);
    const [fleetTopBottomDrivers, setFleetTopBottomDrivers] = useState<Record<string, FleetTopBottomDrivers>>({});
    const [overviewData, setOverviewData] = useState<OverviewData[]>([]);
    const [handlingStatusList, setHandlingStatusList] = useState<Array<string>>([]);
    const [animateMetrics, setAnimateMetrics] = useState<boolean>(true);
    const [animateAccidents, setAnimateAccidents] = useState<boolean>(true);
    const [hiddenSubFleets, setHiddenSubFleets] = useState<string[]>([]);
    const [timespan, setTimespan] = useState<QuarterYear>({ quarter: 0, year: 0 });
    const [timespanOpen, setTimespanOpen] = useState<boolean>(false);
    const [moreTimespans, setMoreTimespans] = useState<number>(0);
    const [csaScores, setCsaScores] = useState<FleetSafetyScores[]>([]);
    const [isCsaLoading, setIsCsaLoading] = useState<boolean>(true);
    const [isCsaError, setIsCsaError] = useState<boolean>(false);
    const isMobile = useIsMobile();
    const history = useHistory();
    const { drivers } = useDrivers();
    let subFleetList = [...new Set(drivers.map((driver) => driver.sub_fleet))].sort((a, b) => a.localeCompare(b));

    // Filter subfleets to only show user's subfleets (or all if admin/empty)
    const subFleetOptions =
        profile.sub_fleets.length > 0
            ? profile.sub_fleets
            : [ALL_FLEET, ...profile.customer.sub_fleets.filter(Boolean)].sort((a, b) => a.localeCompare(b));
    subFleetList = subFleetOptions;

    const isUSFleet = profile.customer.settings.country === 'us';

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        history.push(`/metrics/${TABS[newValue]}`);
    };

    useEffect(() => {
        document.title = `${DEFAULT_TITLE} | ${t(`navigator.${PAGE_DEFS[tab].name}`)}`;
    }, [tab]);

    useEffect(() => {
        if (metricsTab !== undefined && TABS.includes(metricsTab)) {
            setTab(TABS.indexOf(metricsTab));
        } else {
            history.replace('/metrics/safety');
        }
    }, [metricsTab]);

    const getMetrics = () => {
        setIsMetricsError(false);
        setIsMetricsLoading(true);
        Promise.all([api.apiV0FleetScoreGet({ cache: 1 }), api.apiV0FleetTopBottomDriversGet({ cache: 1 })])
            .then(([fleetScoreRes, topBottomDriversRes]) => {
                if (fleetScoreRes.data.length > 0) {
                    setFleetScoreDetails(
                        Object.fromEntries(
                            fleetScoreRes.data.map((fsd) => [!!fsd.sub_fleet ? fsd.sub_fleet : ALL_FLEET, fsd]),
                        ),
                    );
                    setFleetTopBottomDrivers(
                        Object.fromEntries(
                            topBottomDriversRes.data.map((fsd) => [!!fsd.sub_fleet ? fsd.sub_fleet : ALL_FLEET, fsd]),
                        ),
                    );
                    return api
                        .apiV0FleetScoreTrendGet({
                            cache: 1,
                            endDate: Math.max(...fleetScoreRes.data.map((fsd) => fsd.timestamp)),
                        })
                        .then((res) => {
                            setFleetScoreTrend(res.data);
                            setIsMetricsLoading(false);
                            setTimeout(() => setAnimateMetrics(false), ANIMATION_LENGTH);
                        });
                }
                return Promise.reject();
            })
            .catch(() => setIsMetricsError(true));
    };

    const getAccidentOverview = () => {
        setIsOverviewError(false);
        setIsOverviewLoading(true);
        api.apiV0AccidentOverviewGet()
            .then((res) => {
                setOverviewData(res.data.map((d) => (d.sub_fleet === '' ? { ...d, sub_fleet: ALL_FLEET } : d)));
                setIsOverviewLoading(false);
                setTimeout(() => setAnimateAccidents(false), ANIMATION_LENGTH);
            })
            .catch(() => setIsOverviewError(true));
    };

    const getCsaScores = () => {
        setIsCsaLoading(true);
        setIsCsaError(false);
        api.apiV0FleetSafetyScoresGet()
            .then((res) => {
                setCsaScores(res.data);
            })
            .catch(() => setIsCsaError(true))
            .finally(() => setIsCsaLoading(false));
    };

    useEffect(() => {
        getMetrics();
        getAccidentOverview();
        getCsaScores();
    }, []);

    useEffect(() => {
        agencyApi
            .agencyV1CustomerDataGet()
            .then((res) =>
                setHandlingStatusList(res.data.handling_status.map((status: { name: string }) => status.name)),
            );
    }, []);

    useEffect(() => {
        const timestamp = getQuarterTimestamps(timespan)[1];
        const isDataExists = overviewData.find((data) => data.timestamp === timestamp);
        if (timespan.year !== 0 && !isDataExists) {
            const abortController = new AbortController();
            setIsOverviewLoading(true);
            api.apiV0AccidentOverviewTimestampGet({ timestamp }, { signal: abortController.signal })
                .then((res) => {
                    setOverviewData((prevData) =>
                        [...prevData, ...res.data].map((d) =>
                            d.sub_fleet === '' ? { ...d, sub_fleet: ALL_FLEET } : d,
                        ),
                    );
                })
                .finally(() => setIsOverviewLoading(false));
            return () => abortController.abort();
        }
    }, [timespan]);

    return (
        <Box
            sx={{ width: '100%', maxWidth: tab === 2 ? '1000px' : '1200px', display: 'flex', flexDirection: 'column' }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column-reverse' : 'row',
                    width: '100%',
                    justifyContent: 'space-between',
                    alignItems: 'end',
                    mb: 1,
                    borderBottom: `1px solid ${palette.gray[400]}`,
                }}
            >
                <Tabs
                    value={tab}
                    onChange={handleTabChange}
                    TabIndicatorProps={{ sx: { bgcolor: 'secondary.main', height: 3 } }}
                    sx={{ width: isMobile ? '100%' : undefined }}
                    variant={isMobile ? 'fullWidth' : undefined}
                >
                    <Tab
                        label={<Typography fontSize={14}>{t('content.metrics.safety')}</Typography>}
                        {...a11yProps(0)}
                        id="safety-tab"
                        data-testid={`tab-0`}
                    />
                    <Tab
                        label={<Typography fontSize={14}>{t('content.metrics.accidents')}</Typography>}
                        {...a11yProps(1)}
                        id="accidents-tab"
                        data-testid={`tab-1`}
                    />
                    {isUSFleet ? (
                        <Tab
                            label={<Typography fontSize={14}>{t('content.metrics.csa_scores')}</Typography>}
                            {...a11yProps(2)}
                            id="csa-scores-tab"
                            data-testid={`tab-2`}
                        />
                    ) : null}
                </Tabs>
                <Box sx={{ display: 'flex' }}>
                    {tab === 1 ? (
                        <FormControl fullWidth sx={{ width: 200, mb: 1, mr: 1 }}>
                            <InputLabel>{t('content.accident_overview.timespan')}</InputLabel>
                            <Select
                                label={t('content.accident_overview.timespan')}
                                value={`${timespan.quarter}-${timespan.year}`}
                                id="timespan-select"
                                data-testid="timespan-select"
                                onChange={(e) => {
                                    const [quarter, year] = (e.target.value as string).split('-').map(Number);
                                    if (quarter !== 9999 && year !== 9999) {
                                        setTimespan({ quarter, year });
                                        setTimespanOpen(false);
                                    } else {
                                        setMoreTimespans((p) => p + 1);
                                    }
                                }}
                                onClose={(e: React.SyntheticEvent) => {
                                    const target = e.target as HTMLSelectElement;
                                    const value = target.attributes.getNamedItem('data-value')?.value;
                                    if (value !== '9999-9999') {
                                        setTimespanOpen(false);
                                    }
                                }}
                                onOpen={() => setTimespanOpen(true)}
                                variant="outlined"
                                size="small"
                                open={timespanOpen}
                            >
                                <MenuItem id="last-3m-timespan" value="0-0">
                                    {t('content.accident_overview.past_three_months')}
                                </MenuItem>
                                {getPreviousQuarters(dayjs.utc()).map((quarterYear) => (
                                    <MenuItem
                                        id={`quarter-${quarterYear.quarter}-year-${quarterYear.year}-timespan`}
                                        key={`${quarterYear.quarter}-${quarterYear.year}`}
                                        value={`${quarterYear.quarter}-${quarterYear.year}`}
                                    >
                                        {t('content.accident_overview.quarter', {
                                            quarter: quarterYear.quarter,
                                            year: quarterYear.year,
                                        })}
                                    </MenuItem>
                                ))}
                                <Divider />
                                {moreTimespans ? (
                                    <ListSubheader>
                                        {t('content.accident_overview.previous_quarters')}
                                        <Tooltip title={t('content.accident_overview.previous_quarters_tooltip')}>
                                            <InfoOutlinedIcon
                                                sx={{ color: palette.neutral[700], fontSize: 16, ml: 0.25, pb: 0.1 }}
                                            />
                                        </Tooltip>
                                    </ListSubheader>
                                ) : null}
                                {moreTimespans
                                    ? getPreviousQuarters(dayjs.utc(), 5 + moreTimespans * 4)
                                          .slice(5)
                                          .map((quarterYear) => (
                                              <MenuItem
                                                  id={`quarter-${quarterYear.quarter}-year-${quarterYear.year}-timespan`}
                                                  key={`${quarterYear.quarter}-${quarterYear.year}`}
                                                  value={`${quarterYear.quarter}-${quarterYear.year}`}
                                              >
                                                  {t('content.accident_overview.quarter', {
                                                      quarter: quarterYear.quarter,
                                                      year: quarterYear.year,
                                                  })}
                                              </MenuItem>
                                          ))
                                    : null}
                                <MenuItem id="all-time-timespan" value="9999-9999">
                                    {t('content.accident_overview.load_more')}
                                </MenuItem>
                            </Select>
                        </FormControl>
                    ) : null}
                    {tab !== 2 ? (
                        <FormControl sx={{ width: 200, mb: 1 }}>
                            <InputLabel id="sub-fleet-field-label">{t('content.metrics.sub_fleet')}</InputLabel>
                            <Select
                                id="sub-fleet-field"
                                value={selectedSubFleet}
                                onChange={(e: SelectChangeEvent) => {
                                    setHiddenSubFleets((prev) => prev.filter((sf) => sf !== e.target.value));
                                    setSelectedSubFleet(e.target.value);
                                }}
                                labelId="sub-fleet-field-label"
                                label={t('content.metrics.sub_fleet')}
                                size="small"
                            >
                                {subFleetList.map((subFleet) => (
                                    <MenuItem
                                        id={`sub-fleet-${subFleet}-menu-item`}
                                        data-testid={`${subFleet}-item`}
                                        key={subFleet}
                                        value={subFleet}
                                    >
                                        {subFleet}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : null}
                </Box>
            </Box>
            <TabPanel value={tab} index={0} sx={{ width: '100%' }}>
                <MetricsComponent
                    scoreType="safety"
                    selectedSubFleet={selectedSubFleet}
                    setSelectedSubFleet={setSelectedSubFleet}
                    fleetScoreDetails={fleetScoreDetails}
                    fleetScoreTrend={fleetScoreTrend}
                    fleetTopBottomDrivers={fleetTopBottomDrivers}
                    isLoading={isMetricsLoading}
                    isError={isMetricsError}
                    animate={animateMetrics}
                    getMetrics={getMetrics}
                    hiddenSubFleets={hiddenSubFleets}
                    setHiddenSubFleets={setHiddenSubFleets}
                />
            </TabPanel>
            <TabPanel value={tab} index={1} sx={{ width: '100%' }}>
                <AccidentOverview
                    selectedSubFleet={selectedSubFleet}
                    setSelectedSubFleet={setSelectedSubFleet}
                    overviewData={overviewData.filter((data) => data.timestamp === getQuarterTimestamps(timespan)[1])}
                    handlingStatusList={handlingStatusList}
                    setHandlingStatusList={setHandlingStatusList}
                    isLoading={isOverviewLoading}
                    isError={isOverviewError}
                    animate={animateAccidents}
                    getAccidentOverview={getAccidentOverview}
                    hiddenSubFleets={hiddenSubFleets}
                    setHiddenSubFleets={setHiddenSubFleets}
                    timespan={timespan}
                />
            </TabPanel>
            {isUSFleet ? (
                <TabPanel value={tab} index={2} sx={{ width: '100%' }}>
                    <CsaScoresComponent
                        csaScores={csaScores}
                        isLoading={isCsaLoading}
                        isError={isCsaError}
                        onReload={() => getCsaScores()}
                    />
                </TabPanel>
            ) : null}
        </Box>
    );
};

export default MetricsContent;
